// The dodge bot, from the page's side. It runs in a worker with its own build of the VM, so a
// long search never blocks the page, and the page plays the path it finds back tick by tick.
//
// The worker is busy inside one wasm call for the whole search, so it can't hear a message
// asking it to stop. A shared flag it checks as it reports progress can, but shared memory needs
// the page to be cross-origin isolated (see public/_headers). Without that, stopping throws the
// worker away instead.

/** bs-dodge's Settings::default(), for what the settings menu doesn't set. */
export const DODGE_DEFAULTS = { speed: 2.5, beam: 24, horizon: 12, ticks: 1200 };

/** Everything the worker needs to set up the same VM the page would after a reset. */
export interface DodgeRequest {
  source: string;
  seed: number;
  field: { w: number; h: number };
  /** Where main spawns, and as what. */
  enemy: { x: number; y: number; kind: string };
  /** Where the player starts. */
  start: { x: number; y: number };
  /** Named inputs: rank, difficulty and the vars that are set. */
  inputs: [string, number][];
  beat: [number, number] | null;
  settings: { radius: number; speed: number; beam: number; horizon: number; ticks: number };
  /** Hit radii by kind name, and one for any kind not listed. */
  radii: { names: string[]; values: number[]; fallback: number };
}

/** A stretch of ticks where the bot found no move that wasn't hit. */
export interface Stuck {
  from: number;
  to: number;
  /** Where the paths it kept stood just before. */
  positions: [number, number][];
}

export interface DodgeResult {
  /** Where the player stood after each tick, from tick 0: [tick, x, y]. */
  path: [number, number, number][];
  stuck: Stuck[];
  /** How long the search took in the worker. */
  ms: number;
}

export type DodgeReply = ({ type: 'done' } & DodgeResult) | { type: 'failed'; message: string } | { type: 'stopped' };

/** What goes to the worker: the run, its id, and the flag that stops it when set to 1. */
export interface DodgeJob {
  id: number;
  request: DodgeRequest;
  stop: Int32Array | null;
}

/** What comes back: how far a run has got, then how it ended. */
export type WorkerReply = { id: number } & (DodgeReply | { type: 'progress'; percent: number });

interface Run {
  id: number;
  stop: Int32Array | null;
  resolve: (reply: DodgeReply) => void;
  onProgress: (percent: number) => void;
}

/** One run at a time. */
export class Dodger {
  private worker: Worker | null = null;
  private current: Run | null = null;
  private lastId = 0;

  get running(): boolean {
    return this.current !== null;
  }

  run(request: DodgeRequest, onProgress: (percent: number) => void): Promise<DodgeReply> {
    this.cancel();
    this.worker ??= this.spawn();
    const worker = this.worker;
    const stop = crossOriginIsolated ? new Int32Array(new SharedArrayBuffer(4)) : null;
    const id = ++this.lastId;
    return new Promise((resolve) => {
      this.current = { id, stop, resolve, onProgress };
      const job: DodgeJob = { id, request, stop };
      worker.postMessage(job);
    });
  }

  /** Stops the run. Its promise settles as stopped right away; the worker follows shortly. */
  cancel(): void {
    const run = this.current;
    if (!run) return;
    this.current = null;
    if (run.stop) {
      Atomics.store(run.stop, 0, 1);
    } else {
      this.worker?.terminate();
      this.worker = null;
    }
    run.resolve({ type: 'stopped' });
  }

  private spawn(): Worker {
    const worker = new Worker(new URL('./dodge-worker.js', import.meta.url), { type: 'module' });
    worker.addEventListener('message', (e: MessageEvent<WorkerReply>) => this.receive(e.data));
    worker.addEventListener('error', (e) => {
      e.preventDefault();
      this.worker?.terminate();
      this.worker = null;
      const run = this.current;
      this.current = null;
      run?.resolve({ type: 'failed', message: e.message || "the dodge worker didn't start" });
    });
    return worker;
  }

  /** Replies about a run that's since been stopped are old news. */
  private receive(reply: WorkerReply): void {
    const run = this.current;
    if (!run || reply.id !== run.id) return;
    if (reply.type === 'progress') {
      run.onProgress(reply.percent);
      return;
    }
    this.current = null;
    run.resolve(reply);
  }
}
